<template>
  <GenericDialog :activator="activator" @close="closeAndReset">
    <v-card flat>
      <v-card-title class="mb-5" v-if="!planning && !fetching">
        Add a charging stop
      </v-card-title>
      <DescriptiveLoader
        v-if="planning"
        message="Power trip is recalculating your trip"
      />
      <DescriptiveLoader
        v-else-if="fetching"
        message="Power trip is finding all other compatible chargers along your trip"
      />
      <v-card-text v-else-if="!chargersInRange.length">
        There are no compatible chargers in range{{
          tripDetails
            ? "(" +
              Math.round(
                tripDetails.itinerary.totalDrivingDistance / 10 / 1000
              ) +
              "km)"
            : ""
        }}
        of this point in the trip.
      </v-card-text>
      <v-card v-else-if="selectedCharger">
        <v-card class="mb-4 rounded-lg" color="background">
          <v-row no-gutters>
            <v-col cols="1" class="d-flex align-start justify-center">
              <v-img
                contain
                width="42"
                height="42"
                :src="getSrc('icons/DC.svg')"
                class="mt-5"
                aspect-ratio="1"
                max-height="42"
                max-width="42"
              />
            </v-col>
            <v-col cols="10">
              <v-card-title style="word-break: normal">
                {{ selectedCharger.name }}
              </v-card-title>
              <v-card-subtitle>
                {{ selectedCharger.addressString }}
              </v-card-subtitle>
              <v-card-text>
                {{ selectedCharger.evses.length }} charging bays
              </v-card-text>
            </v-col>
          </v-row>
          <v-card-text>
            <v-slider
              v-model="chargeTo"
              min="1"
              max="100"
              step="1"
              track-color="grey lighten-2"
              :label="'Charge up to ' + chargeTo + '%'"
            />
            <v-list class="background">
              <v-subheader>Add to leg</v-subheader>
              <v-list-item-group
                v-model="selectedStep"
                color="primary"
                mandatory
              >
                <v-list-item v-for="(item, i) in stepOrder" :key="i">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.displayName }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <ElevatedBlockBtn class="mt-5" @click="addChargingStop">
              Add charging stop
            </ElevatedBlockBtn>
          </v-card-text>
        </v-card>
      </v-card>
      <v-card-text v-else>
        <v-row no-gutters justify="space-between">
          <v-card
            v-for="charger in chargersInRange"
            :key="charger.id"
            width="49%"
            class="mb-4 rounded-lg"
            color="background"
            @click="selectCharger(charger)"
          >
            <v-row no-gutters>
              <v-col cols="2" class="d-flex align-start justify-center">
                <v-img
                  contain
                  width="42"
                  height="42"
                  :src="getSrc('icons/DC.svg')"
                  class="mt-5"
                  aspect-ratio="1"
                  max-height="42"
                  max-width="42"
                />
              </v-col>
              <v-col cols="10">
                <v-card-title style="word-break: normal">
                  {{ charger.name }}
                </v-card-title>
                <v-card-subtitle>
                  {{ charger.addressString }}
                </v-card-subtitle>
                <v-card-text>
                  {{ charger.evses.length }} charging bays
                </v-card-text>
              </v-col>
            </v-row>
          </v-card>
        </v-row>
      </v-card-text>
    </v-card>
  </GenericDialog>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { mapGetters, mapState } from "vuex";
import { GettersTypes, MutationTypes, State } from "@/logic/store/store_types";
import Charger from "@/logic/classes/charger_classes/charger";
import Trip from "@/logic/classes/trip_classes/trip";
import getAssetSrc from "@/logic/utils/getAssetSrc";
import ElevatedBlockBtn from "../../ui-elements/buttons/ElevatedBlockBtn.vue";
import NetworkBranding from "@/logic/classes/charger_classes/networkBranding";
import GenericDialog from "../../dialog-wrappers/GenericDialog.vue";
import DescriptiveLoader from "../../ui-elements/loaders/DescriptiveLoader.vue";

export default Vue.extend({
  name: "AddChargingStopContent",
  components: {
    ElevatedBlockBtn,
    GenericDialog,
    DescriptiveLoader,
  },
  props: {
    centerLatLng: {
      type: Object as PropType<
        { latitude: number; longitude: number } | undefined
      >,
    },
    activator: Boolean,
  },
  data() {
    return {
      selectedCharger: null as Charger | null,
      chargeTo: 80,
      planning: false,
      selectedStep: 0,
      fetching: false,
    };
  },
  computed: {
    ...mapGetters({
      tripDetails: GettersTypes.selectedTripData,
    }),
    ...mapState({
      networkBrandingList: (state: unknown): NetworkBranding[] =>
        (state as State).networkBranding,
      chargingCostPerMin: (state: unknown): number =>
        (state as State).defaultCostPerMinDC,
      publicCostPerKWh: (state: unknown): number =>
        (state as State).defaultPublicCostPerKWh,
      stations: (state: unknown): Charger[] => (state as State).chargers,
    }),
    chargersInRange(): Charger[] {
      const typedTrip = this.tripDetails as Trip | undefined;
      if (!typedTrip || !this.centerLatLng) return [];
      const chargerIds = typedTrip.findChargerIdsInRangeOfPoint(
        this.centerLatLng,
        Math.floor(typedTrip.itinerary.totalDrivingDistance / 10 / 1000)
      );

      return (
        (this.stations as Charger[]).filter((c) => chargerIds.includes(c.id)) ||
        []
      );
    },
    stepOrder(): {
      distanceScore: number;
      stepIndex: number;
      pointInBounds: boolean;
      displayName: string;
    }[] {
      if (!this.tripDetails) return [];
      if (!this.centerLatLng) return [];
      return (this.tripDetails as Trip).itinerary.orderStepsByDistanceFromPoint(
        {
          lat: this.centerLatLng.latitude,
          lon: this.centerLatLng.longitude,
        }
      );
    },
  },
  methods: {
    getSrc(partialFilePath: string) {
      return getAssetSrc(partialFilePath);
    },
    selectCharger(charger: Charger) {
      this.selectedCharger = charger;
    },
    branding(): NetworkBranding | undefined {
      if (!this.selectedCharger?.operator?.name) return undefined;
      return this.networkBrandingList.find((network) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        network.isThisNetwork(this.selectedCharger!.operator!.name!)
      );
    },
    closeAndReset() {
      this.$emit("close");
      this.selectedCharger = null;
      this.chargeTo = 80;
    },
    async addChargingStop() {
      this.planning = true;
      if (!this.selectedCharger) return;
      if (!this.tripDetails) return;

      const trip = this.tripDetails as Trip;
      await trip.addChargerToStep({
        charger: this.selectedCharger,
        stepIndex: this.stepOrder[this.selectedStep].stepIndex,
        stateOfChargeAfterCharging: this.chargeTo / 100,
        defaultCostPerKWh: this.publicCostPerKWh,
        defaultCostPerMinDC: this.chargingCostPerMin,
      });
      this.$store.commit(MutationTypes.updateIndividualTrip, trip);
      this.closeAndReset();
      this.planning = false;
    },
    async fetchCorridorCall() {
      this.fetching = true;
      const typedTrip = this.tripDetails as Trip | undefined;
      if (!typedTrip) return;
      const outcome = await typedTrip.getRadarData();
      if (outcome === "SUCCESS") {
        this.$store.commit(MutationTypes.updateIndividualTrip, typedTrip);
      }
      this.fetching = false;
    },
  },
  watch: {
    activator() {
      if (this.activator) {
        this.fetchCorridorCall();
      }
    },
  },
});
</script>

<style scoped>
* >>> .v-slider--horizontal {
  margin-left: unset;
  margin-right: unset;
}
* >>> .v-slider--horizontal .v-slider__track-container {
  height: 6px; /* override default slider thickness */
}

* >>> .v-slider__track-fill {
  border-radius: 2px; /* override default slider border-radius */
}

* >>> .v-slider__track-background {
  border-radius: 2px; /* override default slider border-radius */
}
</style>
